import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { usePractice, getTimeSlotStatus } from "@/hooks/usePractice";
import { db, type PracticeEntry } from "@/lib/db";
import { ALL_VOWS } from "@/data/vows";
import { EntryCard } from "@/components/journal/EntryCard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Check,
  Clock,
  Circle,
  AlertCircle,
  FileText,
  HandHeart,
  CheckCircle2,
  BookOpen,
  Plus,
} from "lucide-react";
import { cn } from "@/lib/utils";

// Build a lookup map for virtue names
const virtueNameMap: Record<number, string> = {};
for (const v of ALL_VOWS) {
  virtueNameMap[v.id] = v.de.title;
}

type MainTab = "heute" | "historie";
type HeuteTab = "zeitslots" | "posneg";
type HistorieFilter = "all" | "positive" | "negative" | "promises" | "fulfilled";

function getTodayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function getYesterdayStr(): string {
  const d = new Date(Date.now() - 86400000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateHeader(dateStr: string): string {
  if (dateStr === getTodayStr()) return "Heute";
  if (dateStr === getYesterdayStr()) return "Gestern";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export function DailyOverviewPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    todayEntries,
    completedSlots,
    entriesPerSlot,
    nowMinutes,
    timeSlots,
    loading: todayLoading,
    deleteEntry,
    fulfillPromise,
    fulfillVersprechen,
  } = usePractice();

  const [mainTab, setMainTab] = useState<MainTab>("heute");
  const [heuteTab, setHeuteTab] = useState<HeuteTab>("zeitslots");
  const [historieFilter, setHistorieFilter] = useState<HistorieFilter>("all");
  const [allEntries, setAllEntries] = useState<PracticeEntry[]>([]);
  const [allLoaded, setAllLoaded] = useState(false);
  const [allLoading, setAllLoading] = useState(false);

  const userId = user?.id ?? "";

  // Load all entries when switching to Historie tab
  const loadAllEntries = useCallback(async () => {
    if (!userId || allLoaded) return;
    setAllLoading(true);
    try {
      const entries = await db.practiceEntries
        .where("userId")
        .equals(userId)
        .reverse()
        .sortBy("completedAt");
      setAllEntries(entries);
      setAllLoaded(true);
    } catch {
      // ignore
    } finally {
      setAllLoading(false);
    }
  }, [userId, allLoaded]);

  useEffect(() => {
    if (mainTab === "historie") {
      loadAllEntries();
    }
  }, [mainTab, loadAllEntries]);

  // Open Todos from today's entries (unfulfilled Ausgleichshandlung or Versprechen)
  const openTodos = useMemo(
    () =>
      todayEntries.filter(
        (e) =>
          e.type === "negative" &&
          ((e.versprechen && e.versprechenErfuellt !== true) ||
            (e.promise && e.promiseErfuellt !== true))
      ),
    [todayEntries]
  );

  // Today split by type
  const positiveEntries = useMemo(
    () => todayEntries.filter((e) => e.type === "positive"),
    [todayEntries]
  );
  const negativeEntries = useMemo(
    () => todayEntries.filter((e) => e.type === "negative"),
    [todayEntries]
  );

  // Historie stats
  const historieStats = useMemo(() => {
    const total = allEntries.length;
    const openPromises = allEntries.filter(
      (e) =>
        e.type === "negative" &&
        ((e.versprechen && e.versprechenErfuellt !== true) ||
          (e.promise && e.promiseErfuellt !== true))
    ).length;
    const fulfilledPromises = allEntries.filter(
      (e) =>
        e.type === "negative" &&
        (e.versprechenErfuellt === true || e.promiseErfuellt === true)
    ).length;
    return { total, openPromises, fulfilledPromises };
  }, [allEntries]);

  // Filtered entries for Historie
  const filteredEntries = useMemo(() => {
    let result: PracticeEntry[];
    switch (historieFilter) {
      case "positive":
        result = allEntries.filter((e) => e.type === "positive");
        break;
      case "negative":
        result = allEntries.filter((e) => e.type === "negative");
        break;
      case "promises": {
        const now = new Date();
        result = allEntries
          .filter(
            (e) =>
              e.type === "negative" &&
              ((e.versprechen && e.versprechenErfuellt !== true) ||
                (e.promise && e.promiseErfuellt !== true))
          )
          .sort((a, b) => {
            const urgency = (e: PracticeEntry) => {
              const d1 = e.versprechenBisDatum ? new Date(e.versprechenBisDatum) : null;
              const d2 = e.promiseBisDatum ? new Date(e.promiseBisDatum) : null;
              const earliest = d1 && d2 ? (d1 < d2 ? d1 : d2) : d1 ?? d2;
              if (!earliest) return 3;
              if (earliest < now) return 0;
              const todayStr = getTodayStr();
              const eStr = earliest.toISOString().slice(0, 10);
              if (eStr === todayStr) return 1;
              return 2;
            };
            return urgency(a) - urgency(b);
          });
        break;
      }
      case "fulfilled":
        result = allEntries.filter(
          (e) =>
            e.type === "negative" &&
            (e.versprechenErfuellt === true || e.promiseErfuellt === true)
        );
        break;
      default:
        result = allEntries;
    }
    return result;
  }, [allEntries, historieFilter]);

  // Group by date for Historie
  const dateGroups = useMemo(() => {
    const groups = new Map<string, PracticeEntry[]>();
    for (const entry of filteredEntries) {
      if (!groups.has(entry.date)) groups.set(entry.date, []);
      groups.get(entry.date)!.push(entry);
    }
    return Array.from(groups.entries()).map(([date, entries]) => ({
      date,
      label: formatDateHeader(date),
      entries,
    }));
  }, [filteredEntries]);

  // Handlers for Historie (direct DB, update local state)
  const handleHistorieDelete = useCallback(async (id: string) => {
    await db.practiceEntries.delete(id);
    setAllEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);

  const handleHistorieFulfill = useCallback(async (id: string) => {
    await db.practiceEntries.update(id, { versprechenErfuellt: true });
    setAllEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, versprechenErfuellt: true } : e))
    );
  }, []);

  const handleHistorieFulfillVersprechen = useCallback(async (id: string) => {
    await db.practiceEntries.update(id, { promiseErfuellt: true });
    setAllEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, promiseErfuellt: true } : e))
    );
  }, []);

  // Tab styles
  const mainTabCls = (tab: MainTab) =>
    cn(
      "flex-1 py-2.5 text-sm font-medium text-center rounded-lg transition-all",
      mainTab === tab
        ? "bg-white dark:bg-[#0D1117] shadow-sm text-vajra-blue dark:text-vajra-cyan"
        : "text-gray-500 dark:text-muted-foreground hover:text-gray-700 dark:hover:text-gray-300"
    );

  const heuteTabCls = (tab: HeuteTab) =>
    cn(
      "flex-1 py-1.5 text-xs font-semibold text-center rounded-md transition-all",
      heuteTab === tab
        ? "bg-vajra-blue text-white shadow-sm"
        : "text-gray-500 dark:text-muted-foreground hover:text-gray-700 dark:hover:text-gray-300"
    );

  return (
    <div className="px-4 py-6 space-y-4 pb-24">
      {/* Page title */}
      <h2 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
        Übersicht
      </h2>

      {/* Main tabs: Heute | Historie */}
      <div className="flex bg-gray-100 dark:bg-[#1E3A5F]/20 rounded-xl p-1 gap-1">
        <button type="button" onClick={() => setMainTab("heute")} className={mainTabCls("heute")}>
          Heute
        </button>
        <button
          type="button"
          onClick={() => setMainTab("historie")}
          className={mainTabCls("historie")}
        >
          Historie
        </button>
      </div>

      {/* ── HEUTE TAB ── */}
      {mainTab === "heute" && (
        <>
          {/* Sub tabs */}
          <div className="flex bg-gray-100 dark:bg-[#1E3A5F]/20 rounded-lg p-1 gap-1">
            <button
              type="button"
              onClick={() => setHeuteTab("zeitslots")}
              className={heuteTabCls("zeitslots")}
            >
              Zeitslots
            </button>
            <button
              type="button"
              onClick={() => setHeuteTab("posneg")}
              className={heuteTabCls("posneg")}
            >
              Positiv &amp; Negativ
            </button>
          </div>

          {todayLoading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} className="h-14 w-full rounded-lg" />
              ))}
            </div>
          ) : heuteTab === "zeitslots" ? (
            <ZeitslotView
              timeSlots={timeSlots}
              entriesPerSlot={entriesPerSlot}
              completedSlots={completedSlots}
              nowMinutes={nowMinutes}
              onDelete={deleteEntry}
              onFulfillPromise={fulfillPromise}
              onFulfillVersprechen={fulfillVersprechen}
              onNavigate={(slot, type) =>
                navigate("/entry", { state: { type, slot: String(slot) } })
              }
            />
          ) : (
            <PosNegView
              positiveEntries={positiveEntries}
              negativeEntries={negativeEntries}
              onDelete={deleteEntry}
              onFulfillPromise={fulfillPromise}
              onFulfillVersprechen={fulfillVersprechen}
              onNavigate={(type) => navigate("/entry", { state: { type } })}
            />
          )}

          {/* Offene Verpflichtungen — today's open ausgleich/versprechen */}
          {!todayLoading && openTodos.length > 0 && (
            <div className="space-y-3 pt-2">
              <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                <HandHeart className="h-4 w-4 text-amber-500" />
                Offene Verpflichtungen ({openTodos.length})
              </h3>
              <div className="space-y-2">
                {openTodos.map((entry) => (
                  <EntryCard
                    key={entry.id}
                    entry={entry}
                    onDelete={deleteEntry}
                    onFulfillPromise={fulfillPromise}
                    onFulfillVersprechen={fulfillVersprechen}
                    virtueNames={
                      entry.virtueIds
                        ?.map((id) => virtueNameMap[id])
                        .filter(Boolean) as string[] | undefined
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── HISTORIE TAB ── */}
      {mainTab === "historie" && (
        <HistorieView
          loading={allLoading}
          stats={historieStats}
          filter={historieFilter}
          onFilterChange={setHistorieFilter}
          dateGroups={dateGroups}
          onDelete={handleHistorieDelete}
          onFulfillPromise={handleHistorieFulfill}
          onFulfillVersprechen={handleHistorieFulfillVersprechen}
          onNavigate={() => navigate("/entry")}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Zeitslot View (Heute)
// ---------------------------------------------------------------------------

function ZeitslotView({
  timeSlots,
  entriesPerSlot,
  completedSlots,
  nowMinutes,
  onDelete,
  onFulfillPromise,
  onFulfillVersprechen,
  onNavigate,
}: {
  timeSlots: import("@/hooks/usePractice").TimeSlotConfig[];
  entriesPerSlot: Record<number, PracticeEntry[]>;
  completedSlots: Set<number>;
  nowMinutes: number;
  onDelete: (id: string) => void;
  onFulfillPromise: (id: string) => void;
  onFulfillVersprechen: (id: string) => void;
  onNavigate: (slot: number, type: "positive" | "negative") => void;
}) {
  return (
    <div className="space-y-2">
      {timeSlots.map((ts) => {
        const status = getTimeSlotStatus(ts, completedSlots, nowMinutes);
        const slotEntries = entriesPerSlot[ts.slot] || [];
        return (
          <SlotRow
            key={ts.slot}
            ts={ts}
            status={status}
            entries={slotEntries}
            onDelete={onDelete}
            onFulfillPromise={onFulfillPromise}
            onFulfillVersprechen={onFulfillVersprechen}
            onAddEntry={(type) => onNavigate(ts.slot, type)}
          />
        );
      })}
    </div>
  );
}

function SlotRow({
  ts,
  status,
  entries,
  onDelete,
  onFulfillPromise,
  onFulfillVersprechen,
  onAddEntry,
}: {
  ts: { slot: number; emoji: string; labelShort: string; label: string };
  status: "completed" | "current" | "upcoming" | "missed";
  entries: PracticeEntry[];
  onDelete: (id: string) => void;
  onFulfillPromise: (id: string) => void;
  onFulfillVersprechen: (id: string) => void;
  onAddEntry: (type: "positive" | "negative") => void;
}) {
  const iconEl = {
    completed: <Check className="h-3.5 w-3.5 text-blue-600 dark:text-vajra-blue" />,
    current: <Clock className="h-3.5 w-3.5 text-cyan-600 dark:text-vajra-cyan" />,
    missed: <AlertCircle className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />,
    upcoming: <Circle className="h-3.5 w-3.5 text-gray-400 dark:text-muted-foreground" />,
  };

  const iconBgCls = {
    completed: "bg-blue-500/10 dark:bg-vajra-blue/20",
    current: "bg-cyan-500/10 dark:bg-vajra-cyan/20",
    missed: "bg-orange-500/10",
    upcoming: "bg-gray-100 dark:bg-muted",
  };

  const rowCls = {
    completed: "border-blue-500/20 dark:border-vajra-blue/20",
    current: "border-cyan-500/30 dark:border-vajra-cyan/30",
    missed: "border-orange-500/20",
    upcoming: "border-gray-100 dark:border-[#1E3A5F]/30",
  };

  const timeRange = ts.label.match(/\((.+)\)/)?.[1] || "";
  const entryCount = entries.length;

  return (
    <div className={cn("rounded-lg border px-3 py-3 space-y-2", rowCls[status])}>
      {/* Slot header row */}
      <div className="flex items-center gap-3">
        <span
          className={cn(
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
            iconBgCls[status]
          )}
        >
          {iconEl[status]}
        </span>

        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium text-gray-900 dark:text-white">
            {ts.emoji} {ts.labelShort}
          </span>
          <span className="text-xs text-gray-400 dark:text-muted-foreground ml-2">{timeRange}</span>
        </div>

        {(status === "current" || status === "missed" || status === "completed") && (
          <button
            type="button"
            onClick={() => onAddEntry("positive")}
            className="flex items-center gap-1 text-xs text-vajra-blue dark:text-vajra-cyan shrink-0"
          >
            <Plus className="h-3 w-3" />
            Eintrag
          </button>
        )}
      </div>

      {/* Entries */}
      {entryCount > 0 && (
        <div className="space-y-1.5">
          {entries.map((entry) => (
            <EntryCard
              key={entry.id}
              entry={entry}
              onDelete={onDelete}
              onFulfillPromise={onFulfillPromise}
              onFulfillVersprechen={onFulfillVersprechen}
              virtueNames={
                entry.virtueIds?.map((id) => virtueNameMap[id]).filter(Boolean) as
                  | string[]
                  | undefined
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Positiv & Negativ View (Heute)
// ---------------------------------------------------------------------------

function PosNegView({
  positiveEntries,
  negativeEntries,
  onDelete,
  onFulfillPromise,
  onFulfillVersprechen,
  onNavigate,
}: {
  positiveEntries: PracticeEntry[];
  negativeEntries: PracticeEntry[];
  onDelete: (id: string) => void;
  onFulfillPromise: (id: string) => void;
  onFulfillVersprechen: (id: string) => void;
  onNavigate: (type: "positive" | "negative") => void;
}) {
  const renderSection = (
    entries: PracticeEntry[],
    type: "positive" | "negative",
    label: string,
    color: string
  ) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className={cn("text-sm font-semibold", color)}>
          {label} ({entries.length})
        </h3>
        <button
          type="button"
          onClick={() => onNavigate(type)}
          className={cn("flex items-center gap-1 text-xs", color, "opacity-70 hover:opacity-100")}
        >
          <Plus className="h-3 w-3" />
          Hinzufuegen
        </button>
      </div>
      {entries.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-200 dark:border-[#1E3A5F] py-6 text-center">
          <p className="text-xs text-gray-400 dark:text-muted-foreground">Noch keine Einträge</p>
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => (
            <EntryCard
              key={entry.id}
              entry={entry}
              onDelete={onDelete}
              onFulfillPromise={onFulfillPromise}
              onFulfillVersprechen={onFulfillVersprechen}
              virtueNames={
                entry.virtueIds?.map((id) => virtueNameMap[id]).filter(Boolean) as
                  | string[]
                  | undefined
              }
            />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-5">
      {renderSection(
        positiveEntries,
        "positive",
        "✓ Positiv",
        "text-emerald-600 dark:text-emerald-400"
      )}
      {renderSection(
        negativeEntries,
        "negative",
        "✕ Negativ",
        "text-rose-600 dark:text-rose-400"
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Historie View
// ---------------------------------------------------------------------------

interface DateGroup {
  date: string;
  label: string;
  entries: PracticeEntry[];
}

const HISTORIE_FILTERS: { key: HistorieFilter; label: string }[] = [
  { key: "all", label: "Alle" },
  { key: "positive", label: "✓ Positiv" },
  { key: "negative", label: "✕ Negativ" },
  { key: "promises", label: "📋 Offene Verpflichtungen" },
  { key: "fulfilled", label: "✅ Erfüllt" },
];

function HistorieView({
  loading,
  stats,
  filter,
  onFilterChange,
  dateGroups,
  onDelete,
  onFulfillPromise,
  onFulfillVersprechen,
  onNavigate,
}: {
  loading: boolean;
  stats: { total: number; openPromises: number; fulfilledPromises: number };
  filter: HistorieFilter;
  onFilterChange: (f: HistorieFilter) => void;
  dateGroups: DateGroup[];
  onDelete: (id: string) => void;
  onFulfillPromise: (id: string) => void;
  onFulfillVersprechen: (id: string) => void;
  onNavigate: () => void;
}) {
  if (loading) {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-10 w-full rounded-lg" />
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-24 rounded-lg" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border-gray-200 dark:border-[#1E3A5F]">
          <CardContent className="py-3 px-3 text-center">
            <div className="flex justify-center mb-1">
              <FileText className="h-4 w-4 text-vajra-blue dark:text-vajra-cyan" />
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total}</p>
            <p className="text-xs text-gray-500 dark:text-muted-foreground">Gesamt</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200 dark:border-[#1E3A5F]">
          <CardContent className="py-3 px-3 text-center">
            <div className="flex justify-center mb-1">
              <HandHeart className="h-4 w-4 text-orange-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.openPromises}
            </p>
            <p className="text-xs text-gray-500 dark:text-muted-foreground">Offen</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200 dark:border-[#1E3A5F]">
          <CardContent className="py-3 px-3 text-center">
            <div className="flex justify-center mb-1">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.fulfilledPromises}
            </p>
            <p className="text-xs text-gray-500 dark:text-muted-foreground">Eingehalten</p>
          </CardContent>
        </Card>
      </div>

      {/* Filter pills */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4">
        {HISTORIE_FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => onFilterChange(f.key)}
            className={cn(
              "whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-all border shrink-0",
              filter === f.key
                ? "bg-vajra-blue/10 dark:bg-vajra-blue/20 border-vajra-blue/40 text-vajra-blue dark:text-vajra-cyan"
                : "border-gray-200 dark:border-[#1E3A5F] text-gray-500 dark:text-muted-foreground hover:border-gray-400 dark:hover:border-gray-500"
            )}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Date-grouped entries */}
      {dateGroups.length === 0 ? (
        <Card className="border-gray-200 dark:border-[#1E3A5F]">
          <CardContent className="py-12 flex flex-col items-center gap-3 text-center">
            <BookOpen className="h-12 w-12 text-gray-300 dark:text-muted-foreground/50" />
            <p className="text-gray-500 dark:text-muted-foreground">Keine Einträge gefunden</p>
            <Button
              onClick={onNavigate}
              className="mt-2 bg-vajra-blue hover:bg-vajra-blue/90"
            >
              Ersten Eintrag erstellen
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {dateGroups.map((group) => (
            <div key={group.date} className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  {group.label}
                </h3>
                <span className="text-xs text-gray-400 dark:text-muted-foreground">
                  {group.entries.length}{" "}
                  {group.entries.length === 1 ? "Eintrag" : "Einträge"}
                </span>
              </div>
              <div className="space-y-2">
                {group.entries.map((entry) => (
                  <EntryCard
                    key={entry.id}
                    entry={entry}
                    onDelete={onDelete}
                    onFulfillPromise={onFulfillPromise}
                    onFulfillVersprechen={onFulfillVersprechen}
                    virtueNames={
                      entry.virtueIds
                        ?.map((id) => virtueNameMap[id])
                        .filter(Boolean) as string[] | undefined
                    }
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
